<script lang="ts">
import { computed, defineComponent, inject, PropType, ref } from "vue";
import Steps from "@/components/Steps.vue";
import formatUtils from "@/businesslogic/utils/formatUtils";
import Abschluss from "@/components/abschluss/Abschluss.vue";
import ProduktmoduleVGV from "@/components/module/produktmoduleVGV";
import { NavvItem } from "@/model/NavItem";
import GebaeudedatenUbersicht from "@/components/abschluss/GebaeudeUebersicht.vue";
import Modal from "@/components/Modal.vue";
import storeUtils from "@/businesslogic/utils/storeUtils";
import Zelle from "@/components/grid/Zelle.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zeile from "@/components/grid/Zeile.vue";
import dataUtils from "@/businesslogic/utils/dataUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "AbschlussFH2024",
  props: { navItems: Object as PropType<NavvItem[]> },
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    Abschluss,
    Steps,
    GebaeudedatenUbersicht,
    Modal,
    Zelle,
    TitelZelle,
    Zeile,
  },
  setup(props) {
    const piniaStore = useFormStore();
    const produktmodule = ref(ProduktmoduleVGV);
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const { gt } = dataUtils();

    const { istNurVGV, istNurVHV, istVGVundVHV, enthaeltVHV, enthaeltVGV } = storeUtils();

    const elementarText = computed(() => {
      return piniaStore.state.tarifinformationen.elementar ? " und eine Rückstausicherung ist vorhanden" : "";
    });

    const fehler = ref({} as any);

    const { formatCurrency } = formatUtils();

    const fhVersObjektZusatztext1 = computed(() => {
      const tmpArray: string[] = [];
      let versObjekt = "";
      if (enthaeltVHV(piniaStore.state)) {
        tmpArray.push(
          "Versicherungssumme Hausrat: " +
            formatCurrency(piniaStore.state.ferienhaus.vhv.tarifinformationen.versicherungssumme, 0),
        );
      }
      if (piniaStore.state.tarifinformationen.hausundgrundbesitzerhaftpflicht) {
        tmpArray.push("Deckungssumme Haus- und Grundbesitzerhaftpflicht: " + formatCurrency(10000000, 0));
      }
      if (tmpArray.length) {
        versObjekt += tmpArray.join(", ");
      }
      return versObjekt;
    });

    const fhVersObjektZusatztext2 = computed(() => {
      let versObjekt = "";
      if (piniaStore.state.ferienhaus.oertlicheLage == 1) {
        versObjekt += "Innerhalb geschlossener Ortschaft, ";
      } else if (piniaStore.state.ferienhaus.oertlicheLage == 2) {
        versObjekt += "Außerhalb geschlossener Ortschaft, ";
      }

      versObjekt += "Hausverwaltung: " + (piniaStore.state.ferienhaus.kontrolleHausverwaltung ? "Ja, " : "Nein, ");

      let nutzungsart = piniaStore.state.ferienhaus.vgv.nutzungsart;
      let tageUnbewohnt = piniaStore.state.ferienhaus.vgv.tageUnbewohnt;
      if (istNurVHV(piniaStore.state)) {
        nutzungsart = piniaStore.state.ferienhaus.vhv.nutzungsart;
        tageUnbewohnt = piniaStore.state.ferienhaus.vhv.tageUnbewohnt;
      }
      if (nutzungsart == 1) {
        versObjekt += "überwiegend Eigennutzung, ";
      } else if (nutzungsart == 2) {
        versObjekt += "überwiegend Fremdnutzung, ";
      }
      if (istNurVHV(piniaStore.state)) {
        if (tageUnbewohnt == 3) {
          versObjekt += "mehr als 270 Tage im Jahr nicht bewohnt/vermietet";
        } else if (tageUnbewohnt == 2) {
          versObjekt += "180 - 270 Tage im Jahr nicht bewohnt/vermietet";
        } else if (tageUnbewohnt == 1) {
          versObjekt += "max. 180 Tage im Jahr nicht bewohnt/vermietet";
        }
      } else {
        if (tageUnbewohnt == 3) {
          versObjekt += "weniger als 95 Tage pro Jahr bewohnt";
        } else if (tageUnbewohnt == 2) {
          versObjekt += "mindestens 95 Tage pro Jahr bewohnt";
        } else if (tageUnbewohnt == 1) {
          versObjekt += "mehr als 180 Tage pro Jahr bewohnt";
        }
      }

      return versObjekt;
    });

    const hatZusatzmodule = computed(() => {
      for (const modul of produktmodule.value) {
        if (modul.modulId in piniaStore.state.tarifinformationen) {
          return true;
        }
      }
      return false;
    });

    const zusatzModule = computed(() => {
      let text = "";

      for (const modul of produktmodule.value) {
        if ((piniaStore.state.tarifinformationen as any)[modul.modulId] && modul.modulId != "hausundgrundbesitzer") {
          if (text.length > 0) {
            text += ", ";
          }
          text += modul.name;
        }
      }

      return text;
    });

    const hinweisWeitereUnterlagen = computed(() => {
      if (
        gt(piniaStore.state.ferienhaus.vhv.tarifinformationen.versicherungssumme, 65000) &&
        enthaeltVHV(piniaStore.state)
      ) {
        return (
          "Bitte beachten Sie, dass wir von Ihnen noch Informationen zur Einbruchssicherung benötigen.<br />" +
          "Weitere Informationen erhalten Sie per E-Mail."
        );
      }
      if (!istNurVHV(piniaStore.state)) {
        return "Bitte beachten Sie, dass wir von Ihnen noch Gebäudefotos benötigen. Weitere Informationen erhalten Sie per E-Mail.";
      }

      return undefined;
    });

    const zusaetzlicheSbLw = computed(() => {
      return (
        enthaeltVGV(piniaStore.state) &&
        piniaStore.state.ferienhaus.vgv.tarifinformationen.extraSelbstbeteiligungLeitungswasser > 0
      );
    });

    return {
      props,
      piniaStore,
      fehler,
      formatCurrency,
      hatZusatzmodule,
      zusatzModule,
      fhVersObjektZusatztext1,
      fhVersObjektZusatztext2,
      hinweisWeitereUnterlagen,
      zusaetzlicheSbLw,
      istSichtbar,
      wechsleSichtbarkeit,
      istNurVGV,
      istNurVHV,
      istVGVundVHV,
      elementarText,
    };
  },
});
</script>

<template>
  <steps :active="5" v-bind:items="navItems" @goto-page="$emit('goto-page', $event)" />

  <abschluss
    produkt="fh2024"
    :zeige-versichertes-objekt="true"
    :zeige-tarif="true"
    :zusaetzliche-SbLw="zusaetzlicheSbLw"
    :hinweistext="hinweisWeitereUnterlagen"
    :preis-reduzierung-sanierung="kernsanierung"
    @next-page="$emit('next-page', $event)"
    @goto-page="$emit('goto-page', $event)"
  >
    <template v-slot:versichertes-objekt>
      <gebaeudedaten-ubersicht />
      <div class="u-mar-t-1" v-if="fhVersObjektZusatztext1">
        {{ fhVersObjektZusatztext1 }}
      </div>
      <div class="u-mar-t-1" v-if="fhVersObjektZusatztext2">
        {{ fhVersObjektZusatztext2 }}
      </div>
    </template>

    <template v-slot:zusatzmodule v-if="hatZusatzmodule">
      <br />
      {{ zusatzModule }}
    </template>

    <template
      v-if="piniaStore.state.ferienhaus.vgv.tarifinformationen.extraSelbstbeteiligung"
      v-slot:zusaetzliche-selbstbeteiligung
    >
      <zeile class="u-mar-t-2">
        <titel-zelle :breite="3" class="o-grid__cell--4" :ausrichtung-responsiv="false" :vertikal-zentrieren="false">
          Zu&shy;sätz&shy;liche Selbst&shy;be&shy;teili&shy;gung je Grund&shy;ge&shy;fahr-Schaden&nbsp;
        </titel-zelle>
        <zelle :breite="9" class="o-grid__cell--8" data-cy="zusaetzlicheSb">
          <strong>{{
            formatCurrency(piniaStore.state.ferienhaus.vgv.tarifinformationen.extraSelbstbeteiligung)
          }}</strong>
        </zelle>
      </zeile>
    </template>

    <template v-slot:angaben-bestaetigen>
      <template v-if="istNurVHV(piniaStore.state)">
        Ja, der Antrag wurde gemäß meinen Angaben erstellt. Ich bestätige, dass es sich um ein Massiv- oder Fertighaus
        mit hartem Dach handelt, es sich nicht in einer Kleingartenanlage befindet und dass die
        <a @click.prevent.stop="wechsleSichtbarkeit('mindestsicherungen')"> tariflichen Mindestsicherungen </a>
        erfüllt sind. Auf eine Beratung habe ich verzichtet und damit auf den Schadenersatzanspruch nach §6 Abs. 5 VVG.
      </template>

      <template v-if="istNurVGV(piniaStore.state)">
        Ja, der Antrag wurde gemäß meiner Angaben erstellt. Ich bestätige, dass mein Haus nicht unter Denkmalschutz
        steht, kein Gewerbebetrieb und kein Schwimmbad im Haus vorhanden sind und es sich nicht in einer
        Kleingartenanlage befindet{{ elementarText }}. Auf eine Beratung habe ich verzichtet und damit auf den
        Schadenersatzanspruch nach §6, Abs. 5 VVG.
      </template>

      <template v-if="istVGVundVHV(piniaStore.state)">
        Ja, der Antrag wurde gemäß meiner Angaben erstellt. Ich bestätige, dass mein Haus nicht unter Denkmalschutz
        steht, kein Gewerbebetrieb und kein Schwimmbad im Haus vorhanden sind und es sich nicht in einer
        Kleingartenanlage befindet. Die
        <a @click.prevent.stop="wechsleSichtbarkeit('mindestsicherungen')"> tariflichen Mindestsicherungen </a>
        sind erfüllt{{ elementarText }}. Auf eine Beratung habe ich verzichtet und damit auf den Schadenersatzanspruch
        nach §6, Abs. 5 VVG.
      </template>
    </template>
  </abschluss>

  <modal
    id="mindestsicherungen"
    v-if="istSichtbar('mindestsicherungen')"
    titel="Tarifliche Mindestsicherungen"
    text="Unter einer Mindestsicherung der Wohnung versteht die GEV die Sicherung von Haustüren bzw. Wohnungsabschlusstüren durch ein Zylinderschloss mit Sicherheitsbeschlag (von außen nicht abschraubbar und über bündig montierte – max. 5 mm überstehende – Schließzylinder). Gleiches gilt für die Sicherung von weiteren Außentüren (z.B. Terrassen-, Kellertüren). Trifft das nicht zu, kontaktieren Sie uns bitte unter 040 3766 3366."
    @closeModal="wechsleSichtbarkeit('mindestsicherungen')"
  />
</template>
